/**
 * PhotoItem2 - Core photo item component
 *
 * Presentational component that delegates rendering to template components.
 * Handles template selection and prop forwarding.
 *
 * @module PhotoItem2
 */

import { templates } from './templates';
import { ErrorState } from '../composed/ErrorState';
import type { PhotoItem2Props } from './PhotoItem2.types';

/**
 * PhotoItem2 component
 *
 * Core component that receives data and template props,
 * selects the appropriate template, and passes props through.
 *
 * @example
 * ```typescript
 * <PhotoItem2
 *   data={photoData}
 *   template="photo-only"
 *   isSelected={true}
 *   onClick={handleClick}
 * />
 * ```
 */
export function PhotoItem2({
  data,
  template = 'photo-only',
  isSelected = false,
  selectable = false,
  zoom = 1.0,
  onClick,
  onCritiqueClick,
  className,
  ...restProps
}: PhotoItem2Props) {
  const TemplateComponent = templates[template];

  // Handle invalid template
  if (!TemplateComponent) {
    const availableTemplates = Object.keys(templates).join(', ');
    return (
      <ErrorState
        title="Invalid Template"
        message={`Template "${template}" not found. Available templates: ${availableTemplates}`}
        className="min-h-[200px]"
      />
    );
  }

  // Render selected template with props
  // Feature 081: photo-versioning - Forward additional props (e.g., onContextMenu) to template
  return (
    <TemplateComponent
      data={data}
      isSelected={isSelected}
      selectable={selectable}
      zoom={zoom}
      onClick={onClick}
      onCritiqueClick={onCritiqueClick}
      className={className}
      {...restProps}
    />
  );
}
